// src/components/ProjectBuilder/TaskCard.tsx
import { Card, CardContent, Box, Typography, Button, Chip, Avatar, AvatarGroup } from '@mui/material';
import { Warning, CalendarToday, Visibility } from '@mui/icons-material';
import { format } from 'date-fns';
import { Task } from '../../services/taskApi';

export interface TaskCardProps {
  task: Task;
  onViewProject: (projectId: number) => void;
  onViewTask: (taskId: number, projectId: number) => void;
}

export default function TaskCard({ task, onViewProject, onViewTask }: TaskCardProps) {
  const borderColor = task.isPastDue ? 'error.main' : task.daysUntilDeadline !== undefined && task.daysUntilDeadline <= 7 ? 'warning.main' : 'divider';
  const borderWidth = task.isPastDue ? 4 : task.daysUntilDeadline !== undefined && task.daysUntilDeadline <= 7 ? 2 : 1;

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: `${borderWidth}px solid`,
        borderColor,
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        {/* Task Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {task.isPastDue && <Warning color="error" />}
          {!task.isPastDue && task.daysUntilDeadline !== undefined && task.daysUntilDeadline <= 7 && (
            <CalendarToday color="warning" />
          )}
          <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
            {task.name}
          </Typography>
        </Box>

        {/* Project Context */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {task.projectName} | {task.phaseName}
        </Typography>

        {/* Airport Badge */}
        {task.airportName && (
          <Chip
            label={task.airportName}
            size="small"
            sx={{ mb: 1, mr: 1 }}
            color="primary"
            variant="outlined"
          />
        )}

        {/* Deadline Info */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>Due:</strong> {format(new Date(task.contractedEndDate), 'MMM dd, yyyy')}
            {task.isPastDue && task.daysOverdue !== undefined && (
              <Typography component="span" color="error.main" sx={{ ml: 1 }}>
                ({task.daysOverdue} {task.daysOverdue === 1 ? 'day' : 'days'} overdue)
              </Typography>
            )}
            {!task.isPastDue && task.daysUntilDeadline !== undefined && (
              <Typography component="span" color="warning.main" sx={{ ml: 1 }}>
                ({task.daysUntilDeadline} {task.daysUntilDeadline === 1 ? 'day' : 'days'} until deadline)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Assigned Users */}
        {task.assignedUsers.length > 0 && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Assigned:
            </Typography>
            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
              {task.assignedUsers.map((user) => (
                <Avatar key={user.userId} title={`${user.displayName} (${user.organizationType})`}>
                  {user.displayName?.charAt(0) || user.email.charAt(0)}
                </Avatar>
              ))}
            </AvatarGroup>
            <Typography variant="caption" color="text.secondary">
              {task.assignedUsers.map(u => u.displayName || u.email).join(', ')}
            </Typography>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={() => onViewProject(task.projectId)}
          >
            View Project
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onViewTask(task.taskId, task.projectId)}
          >
            View Task
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

